import { useState } from "react";
import { IoIosLogIn } from "react-icons/io";
import { MdArrowBack, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../context/AuthProvider";
import { AuthApi } from "../../api/common/apiIndex";
import { useMutation } from "@tanstack/react-query";
import PublicShell from "../common/shell/PublicShell";
import "./LoginForm.css";

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [showMfa, setShowMfa] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;
  const { login } = useAuth();

  const activeRolesFrom = (data) => {
    const memberships = Array.isArray(data?.memberships) ? data.memberships : [];
    const active = memberships.find((m) => String(m.businessId) === String(data?.activeBusinessId));
    return Array.isArray(active?.roles) ? active.roles : (Array.isArray(data?.user?.roles) ? data.user.roles : []);
  };

  const redirectAfterLogin = (data) => {
    const roles = activeRolesFrom(data);
    const isGlobalAdmin = !data?.activeBusinessId && roles.includes("GLOBAL_ADMIN");
    const dest = isGlobalAdmin ? "/admin" : (from ?? "/home");
    navigate(dest, { replace: true });
  };

  const verifyMutation = useMutation({
    mutationFn: ({ mfaToken, code, rememberMe }) => AuthApi.verifyTotp({ mfaToken, code, rememberMe }),
    onSuccess: (data) => {
      setInlineError("");
      login(data);
      toast.success(`Welcome back ${data.user.firstName}`);
      setShowMfa(false);
      setCode("");
      setMfaToken("");
      redirectAfterLogin(data);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Invalid code. Please try again.";
      setInlineError(msg);
      toast.error(msg);
    },
  });

  const loginMutation = useMutation({
    mutationFn: AuthApi.login,
    onSuccess: (data) => {
      setInlineError("");
      if (data?.mfaRequired && data?.mfaToken) {
        setMfaToken(data.mfaToken);
        setShowMfa(true);
        return;
      }
      login(data);
      toast.success(`Welcome back ${data.user.firstName}`);
      redirectAfterLogin(data);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Login failed. Please try again.";
      setInlineError(msg);
    },
  });


  const handleSubmit = (e) => {
    e.preventDefault();
    setInlineError("");
    if (!email || !password) {
      setInlineError("Please fill in all fields.");
      return;
    }
    loginMutation.mutate({ email, password, rememberMe });
  };

  const handleVerify = (e) => {
    e?.preventDefault?.();
    setInlineError("");
    if (!code || code.length !== 6) {
      setInlineError("Enter the 6-digit code.");
      return;
    }
    verifyMutation.mutate({ mfaToken, code, rememberMe });
  };

  return (
    <PublicShell className="login-light">
      <form
        onSubmit={showMfa ? handleVerify : handleSubmit}
        className="card shadow-sm border-0 rounded-4 w-100"
        style={{ maxWidth: 360 }}
      >
        <div className="card-body p-3 p-sm-4">
          <h1 className="h4 text-center fw-semibold mb-1">
            {showMfa ? "Verification code" : "Sign in"}
          </h1>
          <p className="text-muted small text-center mb-3">
            {showMfa
              ? "Enter the 6-digit code from your authenticator app."
              : "Access your Baknay workspace."}
          </p>

          {inlineError && (
            <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">
              {inlineError}
            </div>
          )}

          {!showMfa ? (
            <>
              <div className="form-floating mb-2">
                <input
                  id="email"
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
                <label htmlFor="email">Email</label>
              </div>

              <div className="form-floating mb-3 login-password-field">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <label htmlFor="password">Password</label>
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                </button>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 rounded-3 d-flex align-items-center justify-content-center"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending && (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                )}
                <IoIosLogIn className="me-2" />
                Log in
              </button>
              <div className="form-check mt-3 text-start">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="remember-me">
                  Keep me signed in
                </label>
              </div>

              <div className="text-center mt-3 d-flex flex-column gap-2">
                <Link to="/forgot-password" className="text-muted small text-decoration-none">
                  Forgot password?
                </Link>

                <Link to="/business-register" className="text-muted small text-decoration-none">
                  Register your business
                </Link>
              </div>
            </>
          ) : (
            <>
              <input
                type="tel"
                name="otp"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                className="form-control text-center fs-4 mb-3"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
                  setCode(pasted);
                }}
                autoFocus
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="one-time-code"
                aria-label="6-digit authentication code"
              />

              <button
                type="submit"
                className="btn btn-primary w-100 rounded-3"
                disabled={verifyMutation.isPending || code.length !== 6}
              >
                {verifyMutation.isPending ? "Verifying..." : "Verify"}
              </button>

              <button
                type="button"
                className="btn btn-link text-muted small w-100 mt-3 text-decoration-none d-flex align-items-center justify-content-center gap-1"
                onClick={() => {
                  setShowMfa(false);
                  setInlineError("");
                  setCode("");
                  setMfaToken("");
                }}
                disabled={verifyMutation.isPending}
              >
                <MdArrowBack /> Back to sign in
              </button>

              <div className="text-muted small text-center mt-2">
                Lost access? Contact support to recover your account.
              </div>
            </>
          )}
        </div>
      </form>
    </PublicShell>
  );
}
